import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

import {
  Center,
  Loader,
  Paper,
  Group,
  Stack,
  Box,
  Text,
  Title,
  Badge,
  Divider,
  SimpleGrid,
  Alert,
  Button,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

import usePartner from '../hooks/partners/usePartner.ts';
import useChainStoreContract from '../hooks/chain-store-contracts/useChainStoreContract.ts';
import useDownloadChainStoreContractAttachment from '../hooks/chain-store-contracts/useDownloadChainStoreContractAttachment.ts';
import { formatDateTime2 } from '../utils/DateUtil.ts';

import PreviousPageButton from '../components/ui/PreviousPageButton.tsx';
import { RECEIPT_MODE_LABEL } from '../const/labels.ts';

export default function ChainStoreContractInspectPage() {
  const { partnerId: partnerId, id: id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [isPublishedNoticeClosed, setIsPublishedNoticeClosed] = useState(false);

  const { partner, isLoading: isPartnerLoading } = usePartner(partnerId);
  const { data: contract, isLoading: isContractLoading } =
    useChainStoreContract(partnerId, id);
  const {
    mutate: downloadChainStoreContractAttachment,
    isPending: isDownloadChainStoreContractAttachmentPending,
  } = useDownloadChainStoreContractAttachment();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isPartnerLoading || isContractLoading) {
    return (
      <Center style={{ height: '100%' }}>
        <Loader />
      </Center>
    );
  }

  // DRAFT 階段的合約應回到編輯頁面
  if (contract && contract.state === 'DRAFT') {
    navigate(`/partners/${partnerId}/chain-store-contracts/${id}/edit`);
  }

  // consts
  const shouldDisplayPublishedNotice: boolean =
    !!state?.published && !isPublishedNoticeClosed;
  const canDownload: boolean = contract.attachments.length === 1;

  function onDownloadAttachment() {
    if (isDownloadChainStoreContractAttachmentPending) return;

    const attachment = contract.attachments[0];

    downloadChainStoreContractAttachment(
      {
        partnerId: partnerId,
        contractId: contract.id,
        attachmentId: attachment.id,
      },
      {
        onSuccess: (downloadUrl) => {
          // 透過動態建立 <a> 標籤來下載
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = attachment.displayName; // 不設定時，會使用伺服器回傳的檔名
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        },
      },
    );
  }

  function onDuplicate() {
    navigate(`/partners/${partnerId}/chain-store-contracts/new`, {
      state: { cloneId: contract.id },
    });
  }

  return (
    <>
      <PreviousPageButton
        href={`/partners/${partnerId}`}
        content={'返回業者詳情頁'}
      />

      {shouldDisplayPublishedNotice && (
        <Alert
          variant="light"
          color="green"
          withCloseButton
          mb={20}
          icon={<IconInfoCircle />}
          onClose={() => setIsPublishedNoticeClosed(true)}
          title="成功發佈合約"
        />
      )}

      <Stack gap="lg" mb={70}>
        <Paper radius="lg" p="xl" shadow="xs">
          <Group justify="space-between" align="center">
            <Box>
              <Title order={3}>{contract.name}</Title>
              <Text fz={14} c="gray">
                {`${partner.name} | 連鎖店合約`}
              </Text>
            </Box>
            <Badge>
              <Text size="xs" fw={700}>
                合約已發佈
              </Text>
            </Badge>
          </Group>
        </Paper>

        <Paper radius="lg" p="xl" shadow="xs">
          <Text fw={500} fz={14}>
            基本資訊
          </Text>
          <Divider mt={10} mb={20} />
          <SimpleGrid cols={3} verticalSpacing="xl">
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                合約套用方式
              </Text>
              <Text fz={18} fw={400}>
                {contract.profileType === 'DEFAULT'
                  ? '預設合約'
                  : '指定場站合約'}
              </Text>
            </Box>
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                分潤條件
              </Text>
              <Text fz={18} fw={400}>
                {contract.profitSharingMethod === 'FIXED'
                  ? `固定費用 ${contract.basicFixedFee} 元`
                  : `依交易金額百分比 ${contract.basicPercentage} %`}
              </Text>
            </Box>
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                合約生效起迄日
              </Text>
              <Text
                fz={18}
                fw={400}
              >{`${formatDateTime2(contract.startAt)} - ${formatDateTime2(contract.endAt)}`}</Text>
            </Box>
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                發票開立方式
              </Text>
              <Text fz={18} fw={400}>
                {RECEIPT_MODE_LABEL[contract.receiptMode]}
              </Text>
            </Box>
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                請款金額上限
              </Text>
              <Text fz={18} fw={400}>
                {contract.requestAmountLimit || '-'}
              </Text>
            </Box>
          </SimpleGrid>
        </Paper>

        {contract.profileType === 'SPECIFY' && (
          <>
            <Paper radius="lg" p="xl" shadow="xs">
              <Text fw={500} fz={14}>
                指定場站
              </Text>
              <Divider mt={10} mb={20} />
              {contract.chainStores.length === 0
                ? '-'
                : contract.chainStores.map((chainStore) => (
                    <Box
                      bg="gray.1"
                      px="lg"
                      py="sm"
                      mb="sm"
                      key={chainStore.id}
                    >
                      <Text fz={16} fw={400}>
                        {chainStore.name}
                      </Text>
                    </Box>
                  ))}
            </Paper>
          </>
        )}

        <Paper radius="lg" p="xl" shadow="xs">
          <Text fw={500} fz={14}>
            交易通知網址
          </Text>
          <Divider mt={10} mb={20} />
          <SimpleGrid cols={3} verticalSpacing="xl">
            <Box>
              <Text c="gray" fz={14} fw={500} pb={10}>
                連鎖店交易通知網址
              </Text>
              <Text fz={18} fw={400}>
                {!contract.chainStoreNotificationEndpoint
                  ? '-'
                  : contract.chainStoreNotificationEndpoint}
              </Text>
            </Box>
          </SimpleGrid>
        </Paper>
      </Stack>

      <Box
        pos="fixed"
        bottom={0}
        left={0}
        right={0}
        h={70}
        p="md"
        bg="var(--mantine-color-white)"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          zIndex: 100,
        }}
      >
        <Group justify="flex-end">
          <Button
            variant="outline"
            fw={400}
            onClick={onDownloadAttachment}
            disabled={!canDownload}
          >
            下載紙本合約
          </Button>
          <Button variant="outline" fw={400} onClick={onDuplicate}>
            複製合約
          </Button>
        </Group>
      </Box>
    </>
  );
}
